"use client";

import type Country from "@interfaces/Country";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Flex } from "@ecco/ui/flex";
import {
    SelectDropdown,
    SelectDropdownButton,
    SelectDropdownItem,
    SelectDropdownList,
} from "@ecco/ui/select-dropdown";
import { dropdownButtonStyle, linkStyle, selectDropdownListStyle } from "./styles";
import { getPathname, usePathname } from "@i18n/routing";
import CountryFlagWithName from "./CountryFlagWithName";
import { Box } from "@ecco/ui/box";
import { useParams } from "next/navigation";
import { LinkWrapper } from "@ecco/ui/link-wrapper";
import { useBreakpoint } from "@ecco/ui/use-breakpoint";

interface CountrySelectorProps {
    locationList: Country[];
    currentLocationId: string;
}

export const CountrySelector = ({ locationList, currentLocationId }: CountrySelectorProps) => {
    const { get: getMicrocopy, getMultiple } = useMicrocopy();
    const microcopies = (() => {
        return getMultiple(CommonCMS.shared, [
            CommonCMS.openCountrySelector,
            CommonCMS.countrySelector,
        ]);
    })();

    const getAriaLabelByName = (name: string) => {
        return getMicrocopy(CommonCMS.shared, CommonCMS.countrySelectorItem, {
            replacements: {
                countryName: name,
            },
        });
    };

    const currentPathname = usePathname();
    const currentParams = useParams();

    const getLocalisedPathname = (locale: string) => {
        return getPathname({
            locale: locale,
            // @ts-expect-error - typing issue in next-intl expects pathname to be number rather than string
            href: { pathname: currentPathname.toString(), params: currentParams },
        });
    };

    const currentLocation = locationList.find(({ id }) => id === currentLocationId);
    const isDesktop = useBreakpoint("sm");
    const isMobile = !isDesktop;
    return (
        <Flex justifyContent="flex-end" alignItems="center" zIndex={2}>
            <SelectDropdown
                variant="filled"
                eventListeners={{
                    scroll: false,
                }}
                placement={isMobile ? "top-start" : "top-end"}
            >
                {({ isOpen }) => (
                    <>
                        <SelectDropdownButton
                            sx={dropdownButtonStyle}
                            aria-label={microcopies[CommonCMS.openCountrySelector]}
                        >
                            <CountryFlagWithName
                                icon={currentLocation?.icon}
                                name={currentLocation?.name}
                            />
                        </SelectDropdownButton>
                        {isOpen && (
                            <SelectDropdownList
                                aria-label={microcopies[CommonCMS.countrySelector]}
                                p={4}
                            >
                                <Box sx={selectDropdownListStyle} role="menu">
                                    {locationList.map(({ id, name, icon, link }) => {
                                        const isCurrentDomain = !!link.includes(
                                            process.env.NEXT_PUBLIC_HOSTNAME
                                        );
                                        return (
                                            <SelectDropdownItem
                                                value={id}
                                                key={id}
                                                px={0}
                                                as={LinkWrapper}
                                                href={
                                                    isCurrentDomain
                                                        ? getLocalisedPathname(id)
                                                        : link
                                                }
                                                rel={isCurrentDomain ? "nofollow" : undefined}
                                                aria-label={getAriaLabelByName(name)}
                                                sx={linkStyle}
                                            >
                                                <CountryFlagWithName icon={icon} name={name} />
                                            </SelectDropdownItem>
                                        );
                                    })}
                                </Box>
                            </SelectDropdownList>
                        )}
                    </>
                )}
            </SelectDropdown>
        </Flex>
    );
};
